/**
 * Home for TECHNICIAN: open tickets summary + shortcut to service list.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as api from '../../modules/api';
import PageContainer from '../mui-crud/components/PageContainer';
import { stageLabel } from './serviceTicketLabels';
import { getTechWebUrl } from '../../config/runtimeConfig';

export default function TechnicianHomePage() {
  const { t } = useTranslation();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ assigned: 0, inProgress: 0, totalOpen: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.getServiceTickets({ page: 0, size: 100 });
      const list = data?.content || (Array.isArray(data) ? data : []);
      const assigned = list.filter((x) => x.stage === 'ASSIGNED').length;
      const inProgress = list.filter((x) => x.stage === 'IN_PROGRESS').length;
      setStats({
        assigned,
        inProgress,
        totalOpen: assigned + inProgress,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || t('service.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PageContainer title={t('service.techHomeTitle')} breadcrumbs={[]}>
      <Stack spacing={2} sx={{ maxWidth: 560 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography variant="body1" color="text.secondary">
          {t('service.techHomeHint')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                {stageLabel(t, 'ASSIGNED')}
              </Typography>
              <Typography variant="h4">{loading ? '…' : stats.assigned}</Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                {stageLabel(t, 'IN_PROGRESS')}
              </Typography>
              <Typography variant="h4">{loading ? '…' : stats.inProgress}</Typography>
            </CardContent>
          </Card>
        </Stack>
        <Box>
          <Button
            variant="contained"
            size="large"
            fullWidth
            sx={{ minHeight: 48, color: 'primary.contrastText' }}
            onClick={() => {
              const techWebUrl = getTechWebUrl();
              if (techWebUrl) {
                window.location.href = techWebUrl;
                return;
              }
              history.push('/service/tickets');
            }}
          >
            {t('service.techHomeOpenTickets')} ({loading ? '…' : stats.totalOpen})
          </Button>
        </Box>
        {getTechWebUrl() && (
          <Typography variant="caption" color="text.secondary">
            Основной интерфейс исполнителя — отдельный service web.
          </Typography>
        )}
      </Stack>
    </PageContainer>
  );
}
